import React, { useState } from "react";
import { Form, Formik } from "formik";
import { Box, Flex } from "theme-ui";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import SelectSearch from "components/forms/SelectSearch";
import TextInput from "components/forms/TextInput";
import GooglePlaces from "components/forms/GooglePlaces";
import Subtext from "components/typography/Subtext";
import { CheckMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";

import { getLocations, createLocation } from "api/Locations";
import { addPlanSite } from "api/ServicePlans";

/**
 * Add a Location to a plan. Two modes, matching NewWorkOrderModal so
 * the two "get a Location into the module" flows feel identical:
 *
 *   EXISTING — pick a Location already associated with THIS client
 *   (via `associatedClientId`, the location list's client filter).
 *   Scoping to the client is the fix for the old global picker, which
 *   searched a 6k-row table on columns that don't include `name` and
 *   returned nothing useful. A maintenance plan's Locations are almost
 *   always ones the client already has records at, so this is both
 *   correct and short.
 *
 *   NEW — enter a brand-new address (Google Places). Runs the
 *   dedup-checked POST /locations findOrCreate, then attaches. This is
 *   the "the client committed a site JSCRM has never seen" path, which
 *   is the common onboarding case for a fresh plan.
 *
 * Backend `POST /plans/:id/sites` accepts a `locationId` and
 * findOrCreates the LCP for (location, this plan's client) — staff
 * never has to think about LCPs.
 */
export default function AddLocationToPlanModal({
  planId,
  planName,
  clientId,
  onClose,
  onAdded,
}: {
  planId: string;
  planName: string;
  /** The plan's client. Scopes the existing-Location picker and is
   * where a new Location's LCP gets paired. Optional so an older
   * caller that doesn't pass it still renders (new-address only). */
  clientId?: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  const attach = async (locationId: string) => {
    const res = await addPlanSite({ data: { planId, locationId } });
    if (res?.id) {
      toast.add("Location added to plan.", "var(--theme-ui-colors-success)");
      onAdded();
    } else {
      toast.add(
        res?.message || "Error adding Location to plan.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  const submit = async (values: any) => {
    setSubmitting(true);

    if (values.locationMode === "existing") {
      if (!values.locationId) {
        setSubmitting(false);
        toast.add("Pick a Location.", "var(--theme-ui-colors-danger)");
        return;
      }
      await attach(values.locationId);
      setSubmitting(false);
      return;
    }

    // New address → dedup-checked findOrCreate, then attach.
    if (!values.newLocation?.latitude || !values.newLocation?.longitude) {
      setSubmitting(false);
      toast.add("Search and select an address.", "var(--theme-ui-colors-danger)");
      return;
    }
    const loc = await createLocation({
      name: values.newLocationName?.trim() || undefined,
      latitude: values.newLocation.latitude,
      longitude: values.newLocation.longitude,
      address: values.newLocation.address || {},
    });
    if (!loc?.id) {
      setSubmitting(false);
      toast.add(
        loc?.message || "Error creating the Location.",
        "var(--theme-ui-colors-danger)",
      );
      return;
    }
    await attach(loc.id);
    setSubmitting(false);
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>Add Location to plan</ModalHeader>
      <Formik
        initialValues={{
          locationMode: "existing",
          locationId: "",
          newLocation: null as any,
          newLocationName: "",
        }}
        onSubmit={submit}
      >
        {({ values, setFieldValue }) => (
          <Form noValidate autoComplete="off">
            <ModalBody>
              <Subtext
                fontSize={"12px"}
                fontFamily={"Poppins"}
                margin={0}
                marginBottom={"16px"}
                color={themevals.colors.dkgrey}
              >
                Attach a Location to <strong>{planName}</strong>. Pick one this
                client already has, or enter a brand-new address.
              </Subtext>

              <Flex sx={{ gap: "8px", marginBottom: "12px" }}>
                <ModeChip
                  label="Existing Location"
                  active={values.locationMode === "existing"}
                  onClick={() => setFieldValue("locationMode", "existing")}
                />
                <ModeChip
                  label="New address"
                  active={values.locationMode === "new"}
                  onClick={() => setFieldValue("locationMode", "new")}
                />
              </Flex>

              {values.locationMode === "existing" ? (
                <Box>
                  <SelectSearch
                    name="locationId"
                    label="Location"
                    api={getLocations}
                    value={values.locationId}
                    keyValue="id"
                    keyLabel={["name", "address.line1", "address.city", "address.state"]}
                    labelDivider=" · "
                    sortBy="createdAt"
                    sortDir="DESC"
                    preload={!!clientId}
                    params={
                      clientId
                        ? { filters: { associatedClientId: clientId } }
                        : {}
                    }
                    onChange={(loc: any) => setFieldValue("locationId", loc?.id || "")}
                    placeholder={
                      clientId
                        ? "This client's Locations..."
                        : "Search by name or address..."
                    }
                  />
                  {clientId && (
                    <Subtext
                      fontSize={"11px"}
                      fontFamily={"Poppins"}
                      margin={0}
                      marginTop={"6px"}
                      color={themevals.colors.dkgrey}
                    >
                      Showing Locations this client already has records at. Not here? Switch to New address.
                    </Subtext>
                  )}
                </Box>
              ) : (
                <Box>
                  <GooglePlaces
                    name="newLocation"
                    label="Address"
                    required
                    placeholder="Search for the address..."
                    onChange={(v: any) => setFieldValue("newLocation", v || null)}
                  />
                  <Box sx={{ marginTop: "12px" }}>
                    <TextInput
                      name="newLocationName"
                      label="Location name"
                      value={values.newLocationName}
                      onChange={(val: any) => setFieldValue("newLocationName", val)}
                      placeholder="Optional, e.g. The Goddard School #4127"
                    />
                  </Box>
                  <Subtext
                    fontSize={"11px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    marginTop={"6px"}
                    color={themevals.colors.dkgrey}
                  >
                    If this address already exists as a Location, we reuse it instead of creating a duplicate.
                  </Subtext>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <BasicButton
                type="button"
                outline
                scheme="secondary"
                onClick={onClose}
              >
                Cancel
              </BasicButton>
              <BasicButton
                type="submit"
                $submitting={submitting}
                $iconHeight="14px"
                $iconWidth="14px"
                $loadingColor="#fff"
                keepText={true}
              >
                {!submitting && <CheckMarkIcon fill={"#fff"} />}
                Add Location
              </BasicButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

/**
 * Plain <button> rather than theme-ui Box: BoxProps carries neither
 * `type` nor `disabled`, and inside a Formik <Form> a typeless button
 * submits on click.
 */
function ModeChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "1px solid",
        borderColor: active ? "#1a1a1a" : "#D3D1C7",
        background: active ? "#1a1a1a" : "white",
        color: active ? "white" : "#1a1a1a",
        borderRadius: "14px",
        padding: "4px 14px",
        fontSize: "12px",
        fontFamily: "Poppins",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
